<template>
  <div
    id="col-left"
    class="o-layout__item u-display--block u-display--inline-block@md u-12/12 u-3/12@md col-left--box"
    :class="{ open: showFilters }"
  >
    <div class="filters-top">
      <button
        class="filters-close"
        type="button"
        v-if="showFilters"
        @click="closeFilters"
        aria-label="Close filters"
      >
        <i class="fa-regular fa-close"></i>
      </button>
      <button class="edit-search-btn" type="button" @click="$emit('edit-search'); closeFilters()">
        <i class="fa-regular fa-pen-to-square"></i>
        {{ $func.parseIso("editSearch") }}
      </button>
    </div>
    <hr class="filters-sep" />
    <div class="filters">
      <div class="f-head">
        <span class="f-title">{{ $func.parseIso("filters") }}</span>
        <button class="f-reset" type="button" @click="resetFilters">
          {{ $func.parseIso("resetFilters") }}
        </button>
      </div>

      <div class="chips" v-if="chips.length">
        <div class="chip" v-for="(chip, idx) in chips" :key="idx">
          {{ chip.label }}
          <button type="button" @click="removeChip(chip)">×</button>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.weekType }">
        <div
          class="fc-title"
          @click="toggleSection('weekType')"
        >
          {{ $func.parseIso("weekType") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.weekType">
          <div class="fopt" v-for="opt in weekTypeOptions" :key="opt.value">
            <input
              type="checkbox"
              :id="'wt-' + opt.value"
              :value="opt.value"
              v-model="weekType"
              @change="applyFilters"
            />
            <label :for="'wt-' + opt.value">{{ opt.value }}</label>
            <span class="fcnt">{{ opt.total }}</span>
          </div>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.price }">
        <div class="fc-title" @click="toggleSection('price')">
          {{ t("priceFrom") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.price">
          <div class="frange">
            <input
              type="number"
              placeholder="Min"
              v-model.number="priceRange.min"
              @keydown.enter.prevent="applyFilters"
            />
            <span>–</span>
            <input
              type="number"
              placeholder="Max"
              v-model.number="priceRange.max"
              @keydown.enter.prevent="applyFilters"
            />
            <button
              type="button"
              class="frange-btn"
              @click="applyFilters"
              :aria-label="t('search')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </button>
          </div>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.bedrooms }">
        <div class="fc-title" @click="toggleSection('bedrooms')">
          {{ t("bedrooms") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.bedrooms">
          <div class="fopt" v-for="opt in bedroomOptions" :key="opt.value">
            <input
              type="checkbox"
              :id="'bd-' + opt.value"
              :value="opt.value"
              v-model="bedrooms"
              @change="applyFilters"
            />
            <label :for="'bd-' + opt.value">{{ opt.value }}</label>
            <span class="fcnt">{{ opt.total }}</span>
          </div>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.occupancy }">
        <div class="fc-title" @click="toggleSection('occupancy')">
          {{ $func.parseIso("occupancy") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.occupancy">
          <div class="fopt" v-for="opt in occupancyOptions" :key="opt.value">
            <input
              type="checkbox"
              :id="'oc-' + opt.value"
              :value="opt.value"
              v-model="occupancies"
              @change="applyFilters"
            />
            <label :for="'oc-' + opt.value">{{ opt.value }} {{ t("guests") }}</label>
            <span class="fcnt">{{ opt.total }}</span>
          </div>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.kitchen }">
        <div class="fc-title" @click="toggleSection('kitchen')">
          {{ $func.parseIso("kitchen") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.kitchen">
          <div class="fopt" v-for="opt in kitchenOptions" :key="opt.value">
            <input
              type="checkbox"
              :id="'k-' + opt.value"
              :value="opt.value"
              v-model="kitchens"
              @change="applyFilters"
            />
            <label :for="'k-' + opt.value">{{ opt.value }}</label>
            <span class="fcnt">{{ opt.total }}</span>
          </div>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.rating }">
        <div class="fc-title" @click="toggleSection('rating')">
          {{ t("rating") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.rating">
          <div class="rpills">
            <button
              type="button"
              class="rpill"
              :class="{ on: rating === null }"
              @click="setRating(null)"
            >
              {{ t("any") }}
            </button>
            <button
              type="button"
              class="rpill"
              :class="{ on: rating === '3+' }"
              @click="setRating('3+')"
            >
              3.0+
            </button>
            <button
              type="button"
              class="rpill"
              :class="{ on: rating === '4+' }"
              @click="setRating('4+')"
            >
              4.0+
            </button>
            <button
              type="button"
              class="rpill"
              :class="{ on: rating === '4.5+' }"
              @click="setRating('4.5+')"
            >
              4.5+
            </button>
          </div>
        </div>
      </div>

      <div class="fc" v-if="city === 'all'" :class="{ collapsed: collapsedSections.city }">
        <div class="fc-title" @click="toggleSection('city')">
          {{ t("city") }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
        <div class="fc-body" v-show="!collapsedSections.city">
          <div class="ac">
            <input
              class="resort-input"
              type="text"
              :placeholder="t('searchCity')"
              v-model="cityQuery"
              @focus="cityDropdown = true"
              @input="cityDropdown = true"
              @blur="closeCityDropdown"
            />
            <div class="ac-dropdown" v-if="cityDropdown && filteredCities.length">
              <div
                class="ac-opt"
                v-for="c in filteredCities"
                :key="c"
                @mousedown.prevent="selectCity(c)"
              >
                {{ c }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="fc" :class="{ collapsed: collapsedSections.resort }">
        <div class="fc-title">{{ t("resortName") }}</div>
        <div class="fc-body">
          <div class="ac">
            <input
              class="resort-input"
              type="text"
              :placeholder="t('searchResort')"
              v-model="resortQuery"
              @focus="resortDropdown = true"
              @input="resortDropdown = true"
              @blur="closeResortDropdown"
            />
            <div class="ac-dropdown" v-if="resortDropdown && filteredResorts.length">
              <div
                class="ac-opt"
                v-for="r in filteredResorts"
                :key="r"
                @mousedown.prevent="selectResort(r)"
              >
                {{ r }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { setSearchWeekObject, getSearchWeekObject } from "../../utils/sesions";

const I18N = {
  priceFrom: { es: "Precio desde (USD / semana)", en: "Price from (USD / week)" },
  bedrooms: { es: "Habitaciones disponibles", en: "Bedrooms available" },
  guests: { es: "huéspedes", en: "guests" },
  rating: { es: "Calificación", en: "Rating" },
  any: { es: "Cualquiera", en: "Any" },
  city: { es: "Ciudad", en: "City" },
  resortName: { es: "Nombre del Resort", en: "Resort Name" },
  searchCity: { es: "Buscar por ciudad…", en: "Search by city…" },
  searchResort: { es: "Buscar por resort…", en: "Search by resort…" },
  search: { es: "Buscar", en: "Search" },
};

export default {
  props: ["country", "city", "state", "checkOut", "checkIn", "token"],
  data() {
    return {
      weekType: [],
      bedrooms: [],
      kitchens: [],
      occupancies: [],
      priceRange: { min: null, max: null },
      rating: null,
      cityQuery: "",
      resortQuery: "",
      cityDropdown: false,
      resortDropdown: false,
      showFilters: false,
      collapsedSections: {
        weekType: false,
        price: false,
        bedrooms: false,
        occupancy: false,
        kitchen: false,
        rating: false,
        city: false,
        resort: false,
      },
      weekTypeOptions: [],
      bedroomOptions: [],
      kitchenOptions: [],
      occupancyOptions: [],
      resorts: [],
      cities: [],
      iso: null,
    };
  },
  computed: {
    filteredCities() {
      if (!this.cityQuery) return this.cities;
      const q = this.cityQuery.toLowerCase();
      return this.cities.filter((c) => c.toLowerCase().includes(q));
    },
    filteredResorts() {
      if (!this.resortQuery) return this.resorts;
      const q = this.resortQuery.toLowerCase();
      return this.resorts.filter((r) => r.toLowerCase().includes(q));
    },
    chips() {
      const list = [];
      this.weekType.forEach((v) =>
        list.push({ key: "weekType", value: v, label: v }),
      );
      this.bedrooms.forEach((v) =>
        list.push({ key: "bedrooms", value: v, label: v }),
      );
      this.kitchens.forEach((v) =>
        list.push({ key: "kitchens", value: v, label: v }),
      );
      this.occupancies.forEach((v) =>
        list.push({ key: "occupancies", value: v, label: `${v} ${this.t("guests")}` }),
      );
      if (this.rating != null)
        list.push({
          key: "rating",
          value: this.rating,
          label: this.rating,
        });
      if (this.cityQuery)
        list.push({ key: "city", value: this.cityQuery, label: this.cityQuery });
      if (this.resortQuery)
        list.push({
          key: "resort",
          value: this.resortQuery,
          label: this.resortQuery,
        });
      return list;
    },
  },
  async created() {
    this.iso = localStorage.getItem("iso");
    this._onChangeIso = () => {
      this.iso = localStorage.getItem("iso");
      this.$forceUpdate();
    };
    this._onRefreshFilters = (__filters) => {
      this.modifyFilters(__filters);
    };
    this._openFilters = () => {
      this.showFilters = true;
    };
    this.$bus.on("change-iso", this._onChangeIso);
    this.$bus.on("refresh-filters", this._onRefreshFilters);
    this.$bus.on("open-filters", this._openFilters);

    this.checkFiltersCache();
    this.listWeeksResorts();
    this.listWeeksCities();
  },
  beforeDestroy() {
    this.$bus.off("change-iso", this._onChangeIso);
    this.$bus.off("refresh-filters", this._onRefreshFilters);
    this.$bus.off("open-filters", this._openFilters);
  },
  methods: {
    t(key) {
      const entry = I18N[key];
      if (!entry) return key;
      return entry[this.iso] || entry.en;
    },
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },
    closeFilters() {
      this.showFilters = false;
    },
    setRating(v) {
      this.rating = v;
      this.applyFilters();
    },
    selectCity(c) {
      this.cityQuery = c;
      this.cityDropdown = false;
      this.applyFilters();
    },
    selectResort(r) {
      this.resortQuery = r;
      this.resortDropdown = false;
      this.applyFilters();
    },
    closeCityDropdown() {
      setTimeout(() => (this.cityDropdown = false), 150);
    },
    closeResortDropdown() {
      setTimeout(() => (this.resortDropdown = false), 150);
    },
    removeChip(chip) {
      if (chip.key === "weekType")
        this.weekType = this.weekType.filter((v) => v !== chip.value);
      else if (chip.key === "bedrooms")
        this.bedrooms = this.bedrooms.filter((v) => v !== chip.value);
      else if (chip.key === "kitchens")
        this.kitchens = this.kitchens.filter((v) => v !== chip.value);
      else if (chip.key === "occupancies")
        this.occupancies = this.occupancies.filter((v) => v !== chip.value);
      else if (chip.key === "rating") this.rating = null;
      else if (chip.key === "city") this.cityQuery = "";
      else if (chip.key === "resort") this.resortQuery = "";
      this.applyFilters();
    },
    applyFilters() {
      setSearchWeekObject({
        country: this.country,
        city: this.city,
        state: this.state,
        checkIn: this.checkIn,
        checkOut: this.checkOut,
        page: 1,
        limit: 10,
        type: this.weekType.length ? [...this.weekType] : null,
        capacity: this.bedrooms.length ? [...this.bedrooms] : null,
        kitchen: this.kitchens.length ? [...this.kitchens] : null,
        occupancy: this.occupancies.length ? [...this.occupancies] : null,
        resort: this.resortQuery || null,
        cityFilter: this.cityQuery || null,
        priceMin: this.priceRange.min,
        priceMax: this.priceRange.max,
        rating: this.rating,
      });
      this.$bus.emit("change-filter");
      this.listWeeksResorts();
      this.listWeeksCities();
    },
    resetFilters() {
      this.weekType = [];
      this.bedrooms = [];
      this.kitchens = [];
      this.occupancies = [];
      this.priceRange = { min: null, max: null };
      this.rating = null;
      this.cityQuery = "";
      this.resortQuery = "";
      this.applyFilters();
    },
    checkFiltersCache() {
      const filters = getSearchWeekObject(this.$route.query);
      if (filters.type) this.weekType = Array.isArray(filters.type) ? filters.type : [filters.type];
      if (filters.capacity) this.bedrooms = Array.isArray(filters.capacity) ? filters.capacity : [filters.capacity];
      if (filters.kitchen) this.kitchens = Array.isArray(filters.kitchen) ? filters.kitchen : [filters.kitchen];
      if (filters.occupancy) this.occupancies = Array.isArray(filters.occupancy) ? filters.occupancy : [filters.occupancy];
      if (filters.resort) this.resortQuery = filters.resort;
      if (filters.cityFilter) this.cityQuery = filters.cityFilter;
      if (filters.priceMin != null) this.priceRange.min = filters.priceMin;
      if (filters.priceMax != null) this.priceRange.max = filters.priceMax;
      if (filters.rating != null) this.rating = filters.rating;
    },
    modifyFilters(checkFilters) {
      if (checkFilters) {
        this.weekTypeOptions = checkFilters?.InvType || [];
        this.bedroomOptions = checkFilters?.UnitType || [];
        this.kitchenOptions = checkFilters?.KitchenType || [];
        this.occupancyOptions = checkFilters?.MaxOccupancy || [];
      }
      this.$forceUpdate();
    },
    async listWeeksResorts() {
      if (this.country && this.city && this.checkIn && this.checkOut) {
        try {
          let payload = {
            country: this.country,
            city: this.city,
            dateIn: this.checkIn,
            dateOut: this.checkOut,
          };
          if (this.state) payload.state = this.state;
          if (this.weekType.length) payload.InvTypeInDetail = [...this.weekType];
          if (this.occupancies.length) payload.MaxOccupancy = [...this.occupancies];
          if (this.bedrooms.length) payload.UnitType = [...this.bedrooms];
          if (this.kitchens.length) payload.KitchenType = [...this.kitchens];
          if (this.cityQuery) payload.city = this.cityQuery;

          const { data } = await this.$axios.post(`/week/list-resorts`, {
            ...payload,
            GroupBy: "ResortName",
          });
          this.resorts = data.weeks.sort((a, b) => (a > b ? 1 : b > a ? -1 : 0));
        } catch (error) {
          console.log(error);
        }
      }
    },
    async listWeeksCities() {
      if (this.country && this.city && this.checkIn && this.checkOut) {
        try {
          let payload = {
            country: this.country,
            city: this.city,
            dateIn: this.checkIn,
            dateOut: this.checkOut,
          };
          if (this.state) payload.state = this.state;
          if (this.weekType.length) payload.InvTypeInDetail = [...this.weekType];
          if (this.occupancies.length) payload.MaxOccupancy = [...this.occupancies];
          if (this.bedrooms.length) payload.UnitType = [...this.bedrooms];
          if (this.kitchens.length) payload.KitchenType = [...this.kitchens];

          const { data } = await this.$axios.post(`/week/list-resorts`, {
            ...payload,
            GroupBy: "ResortCity",
          });
          this.cities = data.weeks.sort((a, b) => (a > b ? 1 : b > a ? -1 : 0));
        } catch (error) {
          console.log(error);
        }
      }
    },
  },
};
</script>

<style scoped>
.filters-top {
  padding: 16px 16px 12px;
  background: #fff;
  position: relative;
  z-index: 60;
}
.edit-search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #fff;
  border: 1.5px solid #dde3ea;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #00527c;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;
}
.edit-search-btn:hover {
  background: #f4f7fa;
  border-color: #00527c;
}
.filters-sep {
  border: none;
  border-top: 1px solid #eef2f6;
  margin: 0;
}

.filters {
  padding: 16px;
  background: #fff;
  position: relative;
  z-index: 60;
}
.f-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
}
.f-title {
  font-weight: 600;
  font-size: 1.1rem;
}
.f-reset {
  background: none;
  border: none;
  color: #0066cc;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
}
.f-reset:hover {
  text-decoration: underline;
}
.chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 12px 0;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #eef2f7;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
}
.chip button {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}
.chip button:hover {
  color: #000;
}
.fc {
  border-bottom: 1px solid #ededed;
}
.fc-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 14px 0;
  font-weight: 600;
  font-size: 0.95rem;
}
.fc-title svg {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
}
.fc.collapsed .fc-title svg {
  transform: rotate(-90deg);
}
.fc-body {
  padding-bottom: 12px;
}
.fc.collapsed .fc-body {
  display: none;
}
.fopt {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 0;
}
.fopt input[type="checkbox"] {
  accent-color: #0066cc;
}
.fopt label {
  cursor: pointer;
}
.fcnt {
  margin-left: auto;
  color: #666;
  font-size: 0.85rem;
}
.frange {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 0;
}
.frange input {
  width: 90px;
  padding: 0.35rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
}
.frange-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background: #0066cc;
  color: #fff;
  border-radius: 0.25rem;
  cursor: pointer;
  flex-shrink: 0;
}
.frange-btn svg {
  width: 16px;
  height: 16px;
}
.frange-btn:hover {
  background: #0052a3;
}
.rpills {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 6px 0;
}
.rpill {
  padding: 0.35rem 0.85rem;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.85rem;
}
.rpill.on {
  background: #0066cc;
  color: #fff;
  border-color: #0066cc;
}
.ac {
  position: relative;
}
.resort-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  box-sizing: border-box;
}
.ac-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  z-index: 10;
}
.ac-opt {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
}
.ac-opt:hover {
  background: #f0f4f9;
}

.filters-close {
  display: none;
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  z-index: 2;
}
.filters-overlay {
  display: none;
}

@media (max-width: 767px) {
  #col-left {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    max-width: none;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    z-index: 9999;
    background: #fff;
    overflow-y: auto;
  }
  #col-left.open {
    transform: translateX(0);
  }
  .filters-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .filters-overlay {
    display: none;
  }
}
</style>